// Third-party
import React from 'react';
import { connect } from 'react-redux';

// Components
import classNames from 'classnames';
import { SelectableButton } from '../buttons';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { formatLocalizedString, formatMessage } from '../../../utils/i18n';
import Locale from '@/types/locale';
import Questionnaire from '@/types/questionnaire';
import QuestionnaireAnswerItem from '@/types/questionnaire-answer-item';
import DOMPurify from 'dompurify';

type StateProps = {
  locale: Locale;
};

type Props = StateProps & {
  questionnaire: Questionnaire;
  answers: QuestionnaireAnswerItem[];
};

const QuestionnaireAnswersView = (props: Props) => {
  const { answers, questionnaire, locale } = props;

  return (
    <div className="questionnaire-section">
      <div className="questions-container">
        <div className="questionnaire-header d-flex align-items-center">
          <h1 className="flex-grow-1" data-private data-dd-privacy="mask">
            {formatLocalizedString(questionnaire.title, locale)}
          </h1>
        </div>

        {answers.map(answer => {
          const { question } = answer;
          let answerTexts = answer.text ? [answer.text] : null;

          if (!answerTexts && answer.options && answer.options.length > 0) {
            answerTexts = answer.options.map(opt => formatLocalizedString(opt.description, locale));
          }

          return (
            <div key={answer._id} className={classNames('question', 'question-answer-display')} id={`question-${question._id}`}>
              <div className="question-number">
                <div className="question-order">
                  <span>{answers.indexOf(answer) + 1}</span>
                </div>
              </div>
              <div className="question-title">
                <div className="question-title-h1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatLocalizedString(question.title, locale)) }} />
              </div>

              {question.description && (
                <>
                  {question.description.text && (
                    <div className="question-description">
                      <span>{formatLocalizedString(question.description.text, locale)}</span>
                    </div>
                  )}

                  {question.description.imgUrl && (
                    <div className="question-description-image">
                      <img src={formatLocalizedString(question.description.imgUrl, locale)} alt="" />
                    </div>
                  )}
                </>
              )}

              <div className="question-answers">
                {answerTexts &&
                  answerTexts.map(answerText => (
                    <SelectableButton className={classNames('answer-button', 'answer-display')} key={answerText} data-private data-dd-privacy="mask">
                      <span>{answerText}</span>
                    </SelectableButton>
                  ))}

                {answer.notes && (
                  <>
                    <div className="question-notes-title">{formatMessage('Comparison.Questionnaire.Notes.Title')}</div>
                    <SelectableButton className={classNames('answer-button', 'answer-display')} key="notes" data-private data-dd-privacy="mask">
                      <span>{answer.notes}</span>
                    </SelectableButton>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  locale: state.intl.locale,
});

export default connect(mapStateToProps)(QuestionnaireAnswersView);
